import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { SessionAuth } from "./session";

// Session state for the tree: boot the auth layer once, expose status + login/
// logout + the auth handle (which the ApiClient consumes for bearer/refresh/step-up).
type Status = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  auth: SessionAuth;
  status: Status;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ auth, children }: { auth: SessionAuth; children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    let cancelled = false;
    const sync = (): void => {
      if (!cancelled) setStatus(auth.getToken() ? "authenticated" : "anonymous");
    };
    void auth
      .init()
      .then(sync)
      .catch(() => {
        if (!cancelled) setStatus("anonymous");
      });
    // Follow auth-state changes (the OIDC callback redeems the code AFTER init has
    // already resolved anonymous — the in-memory token store means status must track
    // the userLoaded/userUnloaded events, not just the boot-time snapshot).
    const unsubscribe = auth.onUserChanged?.(sync);
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [auth]);

  const login = useCallback(() => auth.login(), [auth]);
  const logout = useCallback(() => auth.logout(), [auth]);

  return (
    <AuthContext.Provider value={{ auth, status, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
